/* import __COLOCATED_TEMPLATE__ from './handoff-to-external-ticket.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type IntlService from 'ember-intl/services/intl';
import type HandoffToExternalTicket from 'embercom/models/operator/visual-builder/step/handoff-to-external-ticket';
import type Integration from 'embercom/models/standalone/integration';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type EditorState from 'embercom/objects/workflows/graph-editor/editor-state';
import {
  HANDOFF_PLATFORM_ICONS,
  HANDOFF_PLATFORM_DISPLAY_NAMES,
  HANDOFF_PLATFORM_COMPONENTS,
} from 'embercom/objects/standalone/generic-handoff-platforms';

interface Args {
  step: HandoffToExternalTicket;
  editorState: EditorState;
  isPreview?: boolean;
  isEditing?: boolean;
  isFocused?: boolean;
  onValidChange?: (isValid: boolean) => void;
  readOnly?: boolean;
}

export default class HandoffToExternalTicketComponent extends Component<Args> {
  @service declare intl: IntlService;
  @service declare finStandaloneService: FinStandaloneService;

  @tracked availableIntegrations: Integration[] = [];
  @tracked isLoading = false;
  @tracked loadError = false;

  get isIntegrationHandoffStep() {
    return !!this.args.step?.integrationId && !!this.args.step?.integrationType;
  }

  get integrationDisplayName() {
    // First try to find by ID, then fall back to type
    let integration;

    if (this.isIntegrationHandoffStep) {
      integration = this.finStandaloneService.standaloneIntegrations.find(
        (i) =>
          i.integrationId === Number(this.args.step.integrationId) &&
          i.integrationType === Number(this.args.step.integrationType),
      );
    }

    return integration?.displayName;
  }

  get stepIcon() {
    return this.isIntegrationHandoffStep
      ? 'ticket'
      : HANDOFF_PLATFORM_ICONS[this.args.step.integrationType];
  }

  get nodeTitle() {
    return this.isIntegrationHandoffStep
      ? this.intl.t('operator.workflows.visual-builder.handoff-to-external-ticket.title', {
          integration: this.integrationDisplayName,
          htmlSafe: true,
        })
      : this.intl.t('operator.workflows.visual-builder.create-external-ticket.title', {
          integration: HANDOFF_PLATFORM_DISPLAY_NAMES[this.args.step.integrationType],
        });
  }

  get openSideSheet() {
    return !this.args.readOnly && !this.isIntegrationHandoffStep;
  }

  get bodyComponent(): string | undefined {
    return this.isIntegrationHandoffStep
      ? undefined
      : HANDOFF_PLATFORM_COMPONENTS[this.args.step.integrationType];
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::GraphEditor::NodeItems::Steps::HandoffToExternalTicket': typeof HandoffToExternalTicketComponent;
  }
}
